import React, { useState } from 'react';
import {
  decimalsToWei,
  isEqualHex,
  LiquidityAmount,
  PercentAmount,
  Token,
} from '@akropolis-web/primitives';
import { Box, Skeleton } from '@mui/material';
import * as R from 'ramda';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { InfoTooltip, Loading, NextLink, PriceHistoryChart, SectionWithTitle } from 'components';
import { MRT_ExpandedState, Table } from 'components/MRT';
import { colors, styled } from 'core/styles';
import { NetworkSlug, PriceProvider, ProductSlug, VersionSlug } from 'domain/types';
import { calculatePercentDeviation, getAmount, makeContractUrl } from 'domain/utils';
import { ERROR_MESSAGES } from 'domain/utils/constants';
import { priceApi } from 'api/modules';
import { combine, Either, isRight, left, right } from 'utils/either';
import { useSubscribable } from 'utils/react';

import { PRICE_PROVIDER_NAMES } from '../../constants';
import { priceChartsColumns } from './priceChartsColumns';

type Props = {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  tokens: Token[];
};

export type TableData = {
  token: Token;
  marketPrice: Either<LiquidityAmount>;
  oraclePrice: Either<LiquidityAmount>;
  priceDeviation: Either<PercentAmount>;
  priceProviderInfo: PriceProvider | null | undefined;
  difference: Either<LiquidityAmount>;
  network: NetworkSlug;
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
};

export function PriceCharts({ productSlug, versionSlug, network, tokens }: Props) {
  const dataRD = useSubscribable(
    () => {
      const tokenAddresses = R.pluck('address', tokens);
      return combineLatest([
        priceApi.getMarketPrices$({ assets: tokenAddresses, network }),
        priceApi.getOraclePrices$({
          productSlug,
          versionSlug,
          network,
          assets: tokenAddresses,
        }),
        priceApi.getOraclePriceProviders$({
          productSlug,
          versionSlug,
          network,
          assets: tokenAddresses,
        }),
      ]).pipe(
        map(([marketPrices, oraclePrices, oraclePriceProviders]) => {
          return tokens
            .map(token => {
              const marketPrice = marketPrices.get(token.address);
              const oraclePrice = oraclePrices.get(token.address);
              const priceProviderInfo = oraclePriceProviders.find(
                x => !!x && isEqualHex(token.address, x.tokenAddress),
              );
              const mPrice = marketPrice?.price
                ? right(getAmount(marketPrice.price, '$').mul(decimalsToWei(18)))
                : left(ERROR_MESSAGES.UNKNOWN_MARKET_PRICE(token.symbol, token.network));
              const oPrice = oraclePrice?.price
                ? right(getAmount(oraclePrice.price, '$').mul(decimalsToWei(18)))
                : left(ERROR_MESSAGES.UNKNOWN_ORACLE_PRICE(token.symbol, token.network));

              const data: TableData = {
                token,
                marketPrice: mPrice,
                oraclePrice: oPrice,
                priceDeviation: combine(oPrice, mPrice).map(([oracle, market]) =>
                  calculatePercentDeviation(oracle, market),
                ),
                priceProviderInfo,
                difference: combine(oPrice, mPrice).map(([oracle, market]) =>
                  getAmount(oracle.sub(market).abs(), '$'),
                ),
                network,
                productSlug,
                versionSlug,
              };
              return data;
            })
            .filter(({ marketPrice, oraclePrice }) => isRight(marketPrice) && isRight(oraclePrice));
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [priceApi, R.toString(tokens), productSlug, versionSlug, network],
    { clientOnly: true },
  );

  return (
    <Loading
      data={dataRD}
      loader={
        <Skeleton variant="rectangular" height={300} sx={{ backgroundColor: colors.smokyWhite }} />
      }
    >
      {data =>
        data.length > 0 ? (
          <SectionWithTitle title="Market vs oracle price">
            <ChartsTable
              data={data}
              network={network}
              productSlug={productSlug}
              versionSlug={versionSlug}
            />
          </SectionWithTitle>
        ) : null
      }
    </Loading>
  );
}

function ChartsTable({
  data,
  network,
  productSlug,
  versionSlug,
}: {
  data: TableData[];
  network: NetworkSlug;
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
}): JSX.Element | null {
  const pageSize = 5;
  const displaysFiltersAndPagination = data.length > pageSize;

  const [expandedState, setExpandedState] = useState<MRT_ExpandedState>({
    0: true,
  });

  return (
    <Table<TableData>
      initialState={{
        density: 'compact',
        columnOrder: ['mrt-row-expand', 'asset', 'marketPrice', 'oraclePrice', 'difference'],
        pagination: {
          pageIndex: 0,
          pageSize,
        },
      }}
      state={{
        expanded: expandedState,
      }}
      autoPinningColumns={['mrt-row-expand', 'asset']}
      displayColumnDefOptions={{
        'mrt-row-expand': {
          muiTableBodyCellProps: {
            sx: {
              verticalAlign: 'top',
              paddingTop: '3px',
            },
          },
        },
      }}
      data={data}
      columns={priceChartsColumns}
      enableExpanding
      paginateExpandedRows={false}
      maxLeafRowFilterDepth={0}
      onExpandedChange={stateFn => {
        const state = typeof stateFn === 'function' ? stateFn(expandedState) : stateFn;
        const nextState = R.omit(Object.keys(expandedState), state);

        setExpandedState(Object.keys(nextState).length > 0 ? nextState : state);
      }}
      enablePagination={displaysFiltersAndPagination}
      enableColumnFilterModes
      enableFilters={displaysFiltersAndPagination}
      withFullScreenButton
      enablePinning={false}
      enableTopToolbar={false}
      enableToolbarInternalActions={false}
      renderDetailPanel={({ row }) => {
        return (
          <Box
            sx={{
              margin: 'auto',
              width: '100%',
            }}
          >
            <OraclePriceProviders>
              <PriceProvidersTitle>Price sources</PriceProvidersTitle>
              <PriceSource
                priceProviderInfo={row.original.priceProviderInfo}
                network={row.original.token.network}
              />
            </OraclePriceProviders>
            <ChartContainer>
              <AsyncPriceHistoryChart
                network={network}
                productSlug={productSlug}
                versionSlug={versionSlug}
                priceDeviation={row.original.priceDeviation}
                tokenAddress={row.original.token.address}
              />
            </ChartContainer>
          </Box>
        );
      }}
      muiTableBodyCellProps={() => ({
        sx: {
          verticalAlign: 'top',
        },
      })}
    />
  );
}

export function PriceSource({
  priceProviderInfo,
  network,
}: {
  priceProviderInfo?: PriceProvider | null;
  network: NetworkSlug;
}) {
  if (!priceProviderInfo) {
    return <span>{PRICE_PROVIDER_NAMES.Unknown}</span>;
  }

  switch (priceProviderInfo.type) {
    case 'FixedPrice':
      return (
        <div>
          {PRICE_PROVIDER_NAMES[priceProviderInfo.type]}
          &nbsp;
          <InfoTooltip
            title="Price is hard-pegged to USD/ETH by smart contract"
            sx={{ fontSize: 15, marginTop: -0.5 }}
          />
        </div>
      );

    case 'Disabled':
    case 'Unknown': {
      return <div>{PRICE_PROVIDER_NAMES[priceProviderInfo.type]}</div>;
    }

    case 'Forwarded':
    case 'UniswapV3':
    case 'BalancerV2':
    case 'CustomFeedOnchain':
    case 'CustomFeedOffchain':
    case 'LpToken':
    case 'UniswapV3Forwarded':
    case 'ChainlinkV3': {
      const providerAddresses =
        priceProviderInfo.type === 'UniswapV3Forwarded'
          ? priceProviderInfo.payload.slice(1)
          : [priceProviderInfo.payload].flat();

      const providerName =
        priceProviderInfo.type === 'LpToken'
          ? PRICE_PROVIDER_NAMES.Forwarded
          : PRICE_PROVIDER_NAMES[priceProviderInfo.type];

      return (
        <div>
          {providerAddresses.map((x, i) => (
            <div key={x}>
              <OraclePriceProviderLink
                href={makeContractUrl(x, network)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div>{providerName}</div>
              </OraclePriceProviderLink>
              {i !== providerAddresses.length - 1 && ' + '}
            </div>
          ))}
        </div>
      );
    }
  }
}

export function AsyncPriceHistoryChart({
  priceDeviation,
  network,
  productSlug,
  versionSlug,
  tokenAddress,
}: {
  priceDeviation: Either<PercentAmount>;
  network: NetworkSlug;
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  tokenAddress: string;
}) {
  const dataRD = useSubscribable(
    () => {
      const to = new Date();
      const from = new Date(to.getTime() - 8 * 60 * 60 * 1000);
      const timeInterval = 60 * 1000;

      return combineLatest([
        priceApi.getMarketPriceHistory$({
          tokenAddress,
          network,
          from,
          to,
        }),
        priceApi.getOraclePriceHistory$({
          productSlug,
          versionSlug,
          network,
          tokenAddress,
          from,
          to,
          timeInterval,
        }),
      ]).pipe(
        map(([marketPriceHistory, oraclePriceHistory]) => {
          return { marketPriceHistory, oraclePriceHistory };
        }),
      );
    },
    [network, productSlug, tokenAddress, versionSlug],
    { clientOnly: true },
  );

  return (
    <Loading data={dataRD} loader={<noscript />}>
      {({ marketPriceHistory, oraclePriceHistory }) => (
        <PriceHistoryChart
          oracle={oraclePriceHistory}
          market={marketPriceHistory}
          deviation={priceDeviation}
        />
      )}
    </Loading>
  );
}

export const PriceProvidersTitle = styled('div')({
  opacity: 0.5,
  lineHeight: 1,
});

export const OraclePriceProviders = styled('div')({
  width: '100%',
  marginTop: 10,
  marginLeft: 10,
  fontSize: 12,
  display: 'flex',
  gap: 10,
  alignItems: 'center',
});

export const OraclePriceProviderLink = styled(NextLink)(({ theme }) => ({
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 8,
  fontSize: 12,
  lineHeight: 'normal',
  background: colors.silver,
  textDecoration: 'underline',
  transition: theme.transitions.create(['background', 'color']),

  '&:hover': {
    background: colors.grayOpacity[24],
    color: colors.blackOpacity[68],
  },
}));

export const ChartContainer = styled('div')(({ theme }) => ({
  fontSize: 12,
  height: 180,
  width: '82vw',
  margin: '4px 0 0',
  padding: '10px',

  [theme.breakpoints.up('tabletXS')]: {
    width: '690px',
  },
}));
